import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import useAdminStore from '../store/adminStore';
import { db } from '../config/firebase';
import { doc, setDoc, getDoc, collection, addDoc, getDocs, updateDoc, deleteDoc, query, orderBy } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { PE_LOGO_PUBLIC_URL } from '../constants/brandAssets';
import {
    collectCrmRecipientNames,
    groupRecipientsByFirstName,
    htmlAlreadyHasHallo,
    injectHalloGreetingHtml,
    parseExternalLeadNames,
} from '../utils/mailGreeting';

import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// --- KAPSULIERTE ICONS FÜR ISOLATION ---
const IconBase = ({ children, size = 20, className = "" }: any) => <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>{children}</svg>;
const XIcon = (props: any) => <IconBase {...props}><path d="M18 6 6 18"/><path d="m6 6 12 12"/></IconBase>;
const MailIcon = (props: any) => <IconBase {...props}><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></IconBase>;
const SendIcon = (props: any) => <IconBase {...props}><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></IconBase>;
const SaveIcon = (props: any) => <IconBase {...props}><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></IconBase>;
const ImageIcon = (props: any) => <IconBase {...props}><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></IconBase>;
const FilterIcon = (props: any) => <IconBase {...props}><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></IconBase>;
const TrashIcon = (props: any) => <IconBase {...props}><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></IconBase>;
const BriefcaseIcon = (props: any) => <IconBase {...props}><rect width="20" height="14" x="2" y="7" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></IconBase>;
const GlobeIcon = (props: any) => <IconBase {...props}><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></IconBase>;
const RefreshIcon = (props: any) => <IconBase {...props}><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></IconBase>;
const CheckCircleIcon = (props: any) => <IconBase {...props}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><path d="m9 11 3 3L22 4"/></IconBase>;
const ActivityIcon = (props: any) => <IconBase {...props}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></IconBase>;
const EditIcon = (props: any) => <IconBase {...props}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></IconBase>;
const BookIcon = (props: any) => <IconBase {...props}><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></IconBase>;
const FileTextIcon = (props: any) => <IconBase {...props}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></IconBase>;
const ArchiveIcon = (props: any) => <IconBase {...props}><polyline points="21 8 21 21 3 21 3 8"/><rect width="22" height="5" x="1" y="3" rx="1"/><line x1="10" y1="12" x2="14" y2="12"/></IconBase>;
const ClockIcon = (props: any) => <IconBase {...props}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></IconBase>;
const CopyIcon = (props: any) => <IconBase {...props}><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></IconBase>;
const FlaskIcon = (props: any) => <IconBase {...props}><path d="M9 3H15M10 3V14L5.5 19.5A2 2 0 0 0 7 23H17A2 2 0 0 0 18.5 19.5L14 14V3M10 14H14"/></IconBase>; 

interface AdminNewsletterModalProps {
    isOpen: boolean;
    onClose: () => void;
}

type PastedMandant = { email: string; };
type Campaign = {
    id: string;
    status: 'draft' | 'sent';
    subject: string;
    message: string;
    targetClients: boolean;
    targetExternal: boolean;
    updatedAt: string;
    sentAt?: string;
    imageUrls?: string[];
};

const AdminNewsletterModal: React.FC<AdminNewsletterModalProps> = ({ isOpen, onClose }) => {
    const { crmClients, pendingApprovals } = useAdminStore();

    // --- NAVIGATION & UI STATE ---
    const [activeTab, setActiveTab] = useState<'compose' | 'contacts' | 'drafts' | 'archive'>('compose');
    const [isGlobalLoading, setIsGlobalLoading] = useState(false);
    const [statusMsg, setStatusMsg] = useState<{msg: string, type: 'success'|'error'|'info'} | null>(null);

    // --- STATE: EDITOR (COMPOSE) ---
    const [currentDraftId, setCurrentDraftId] = useState<string | null>(null);
    const [targetClients, setTargetClients] = useState(false);
    const [targetExternal, setTargetExternal] = useState(false); 
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    
    const [existingImageUrls, setExistingImageUrls] = useState<string[]>([]);
    const [images, setImages] = useState<File[]>([]);
    const [imagePreviews, setImagePreviews] = useState<string[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const quillRef = useRef<ReactQuill>(null);

    const [liveRecipientCount, setLiveRecipientCount] = useState(0);
    const [testEmail, setTestEmail] = useState('');
    const [isTestSending, setIsTestSending] = useState(false);

    // --- STATE: ADRESSBÜCHER (CONTACTS) ---
    const [rawMandantenInput, setRawMandantenInput] = useState('');
    const [pastedMandanten, setPastedMandanten] = useState<PastedMandant[]>([]);
    const [rawExternalInput, setRawExternalInput] = useState('');
    const [externalLeads, setExternalLeads] = useState<string[]>([]);
    const [externalLeadNames, setExternalLeadNames] = useState<Record<string, string>>({});
    const [contactsChanged, setContactsChanged] = useState(false);

    // --- STATE: KAMPAGNEN (DRAFTS / ARCHIVE) ---
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);

    const showMsg = useCallback((msg: string, type: 'success'|'error'|'info' = 'success') => {
        setStatusMsg({ msg, type });
        setTimeout(() => setStatusMsg(null), 3000);
    }, []);

    // 💉 CHIRURGIE: DIREKT-LÖSCHEN VON INLINE-BILDERN
    const handleEditorImageClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        const target = e.target as HTMLElement;
        if (target.tagName === 'IMG' && target.closest('.ql-editor')) {
            if (window.confirm("Möchtest du dieses Bild aus dem Newsletter entfernen?")) {
                target.remove(); 
                if (quillRef.current) {
                    const quill = quillRef.current.getEditor();
                    setMessage(quill.root.innerHTML); 
                    showMsg("Bild erfolgreich gelöscht.", "info");
                }
            }
        }
    }, [showMsg]);

    useEffect(() => {
        const recipientsSet = new Set<string>();
        recipientsSet.add("erfolg@friseur-coaching.de"); 
        
        if (targetClients) {
            const allClients = [...(crmClients || []), ...(pendingApprovals || [])];
            allClients.forEach(c => {
                if (c.email) recipientsSet.add(c.email.toLowerCase().trim());
                if (c.clientData?.mailadresse) recipientsSet.add(c.clientData.mailadresse.toLowerCase().trim());
            });
            
            pastedMandanten.forEach(m => {
                recipientsSet.add(m.email.toLowerCase().trim());
            });
        }

        if (targetExternal) {
            externalLeads.forEach(e => recipientsSet.add(e.toLowerCase().trim()));
        }

        setLiveRecipientCount(recipientsSet.size + 4); 
    }, [targetClients, targetExternal, crmClients, pendingApprovals, pastedMandanten, externalLeads]);

    const imageHandler = useCallback(() => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.click();

        input.onchange = async () => {
            if (input.files && input.files[0]) {
                const file = input.files[0];
                showMsg("Lade Bild in Text hoch...", "info");
                try {
                    const storage = getStorage(db.app);
                    const safeName = file.name.replace(/[^a-zA-Z0-9.]/g, '');
                    const fileRef = ref(storage, `newsletter_media/inline_${Date.now()}_${safeName}`);
                    await uploadBytes(fileRef, file);
                    const url = await getDownloadURL(fileRef);

                    const quill = quillRef.current?.getEditor();
                    if (quill) {
                        const range = quill.getSelection(true);
                        quill.insertEmbed(range.index, 'image', url);
                        quill.setSelection(range.index + 1, 0); 
                    }
                    showMsg("Bild eingefügt! (Klicke darauf zum Löschen)", "success");
                } catch (err) {
                    console.error("Fehler beim Inline-Bild-Upload:", err);
                    showMsg("Fehler beim Bild-Upload.", "error");
                }
            }
        };
    }, [showMsg]);

    const quillModules = useMemo(() => ({
        toolbar: {
            container: [
                [{ 'header': [1, 2, 3, false] }],
                ['bold', 'italic', 'underline', 'strike'],
                [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                ['link', 'image'], 
                ['clean']
            ],
            handlers: {
                image: imageHandler
            }
        }
    }), [imageHandler]);

    const optimizeHtmlForEmail = (rawHtml: string) => {
        if (!rawHtml) return '';

        // Alte externe PE-Logos → eine Hosting-Quelle
        const normalizedHtml = rawHtml
            .replace(/https?:\/\/i\.imgur\.com\/vXdNPiq\.png/gi, PE_LOGO_PUBLIC_URL)
            .replace(
                /https?:\/\/www\.friseur-coaching\.de\/wp-content\/uploads\/[^"'>\s]*logo[^"'>\s]*\.png/gi,
                PE_LOGO_PUBLIC_URL
            );

        // Bilder als Block (zentriert) — Text läuft darunter in voller Breite, kein gequetschter Float-Spalten-Text.
        const withImages = normalizedHtml.replace(/<img\s([^>]*)>/gi, (_match, attrs: string) => {
            const cleaned = String(attrs || '')
                .replace(/\sstyle=(["'])[\s\S]*?\1/gi, '')
                .replace(/\sclass=(["'])[\s\S]*?\1/gi, '')
                .trim();
            return (
                `<img class="pe-nl-img" ${cleaned} ` +
                `style="display:block;max-width:320px;width:100%;height:auto;margin:8px auto 24px;` +
                `border-radius:8px;border:2px solid #E6C299;" />`
            );
        });

        const mobileCss =
            `<style type="text/css">` +
            `@media only screen and (max-width: 620px) {` +
            `.pe-nl-img {` +
            `max-width:100% !important;` +
            `margin:8px auto 20px !important;` +
            `}` +
            `}` +
            `</style>`;

        const header =
            `<div style="text-align:center;margin:0 0 24px 0;padding-bottom:16px;border-bottom:1px solid #333;">` +
            `<img src="${PE_LOGO_PUBLIC_URL}" alt="PreisExpert" ` +
            `style="max-height:72px;width:auto;max-width:240px;display:inline-block;" />` +
            `</div>`;

        // Header nur einmal, falls der Body schon das neue Logo oben hat
        const hasPeHeader = withImages.includes(PE_LOGO_PUBLIC_URL);
        return mobileCss + (hasPeHeader ? '' : header) + withImages;
    };

    const getSystemEmails = useCallback(() => {
        const allInternalMails = new Set<string>();
        const allClients = [...(crmClients || []), ...(pendingApprovals || [])];
        allClients.forEach(c => {
            if (c.email) allInternalMails.add(c.email.toLowerCase().trim());
            if (c.clientData?.mailadresse) allInternalMails.add(c.clientData.mailadresse.toLowerCase().trim());
        });
        return allInternalMails;
    }, [crmClients, pendingApprovals]);

    /** Gruppe A (CRM/Pending) ∪ Gruppe B (pastedMandanten) — lowercase/trim */
    const getAllMandantenEmails = useCallback((extraPasted?: PastedMandant[]) => {
        const mandantenSet = getSystemEmails();
        const pastedSource = extraPasted ?? pastedMandanten;
        pastedSource.forEach((m) => {
            if (m?.email) mandantenSet.add(String(m.email).toLowerCase().trim());
        });
        return mandantenSet;
    }, [getSystemEmails, pastedMandanten]);

    /** Physische Bereinigung von Gruppe C gegen Mandanten-Set */
    const purgeExternalLeadsAgainstMandanten = useCallback(
        (leads: string[], mandantenSet: Set<string>) => {
            const cleaned = Array.from(
                new Set(
                    (leads || [])
                        .map((e) => String(e || '').toLowerCase().trim())
                        .filter((e) => e.includes('@') && !mandantenSet.has(e))
                )
            ).sort((a, b) => a.localeCompare(b));
            const removedCount = (leads || []).length - cleaned.length;
            return { cleaned, removedCount };
        },
        []
    );

    const persistGlobalContacts = useCallback(
        async (
            nextPasted: PastedMandant[],
            nextExternal: string[],
            opts?: { silent?: boolean }
        ) => {
            const docRef = doc(db, 'newsletter_system', 'global_contacts');
            await setDoc(
                docRef,
                {
                    pastedMandanten: nextPasted,
                    externalLeads: nextExternal,
                    lastCleaned: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                },
                { merge: true }
            );
            if (!opts?.silent) {
                showMsg('Adressbücher bereinigt & synchronisiert.');
            }
        },
        [showMsg]
    );

    const loadGlobalContacts = useCallback(async () => {
        setIsGlobalLoading(true);
        try {
            const docRef = doc(db, 'newsletter_system', 'global_contacts');
            const docSnap = await getDoc(docRef);
                        
            let loadedMandanten: PastedMandant[] = [];
            let loadedLeads: string[] = [];

            if (docSnap.exists()) {
                const data = docSnap.data();
                // Abwärtskompatibilität: Falls alte Struktur (mit type) geladen wird, mappen wir sie einfach um.
                loadedMandanten = (data.pastedMandanten || []).map((m: any) => ({ email: m.email || m })); 
                loadedLeads = data.externalLeads || [];
                setExternalLeadNames(parseExternalLeadNames(data.externalLeadNames));
            } else {
                setExternalLeadNames({});
            }

            const systemMails = getSystemEmails();
            let needsUpdate = false;

            const cleanMandanten = loadedMandanten.filter(m => {
                const email = String(m.email || '').toLowerCase().trim();
                if (!email || systemMails.has(email)) { needsUpdate = true; return false; }
                return true;
            }).map(m => ({ email: String(m.email).toLowerCase().trim() }))
              .sort((a, b) => a.email.localeCompare(b.email));

            // A ∪ B (geladen) → C physisch purgen
            const mandantenSet = getAllMandantenEmails(cleanMandanten);
            const { cleaned: cleanLeads, removedCount } = purgeExternalLeadsAgainstMandanten(
                loadedLeads,
                mandantenSet
            );
            if (removedCount > 0) needsUpdate = true;

            setPastedMandanten(cleanMandanten);
            setExternalLeads(cleanLeads);

            if (needsUpdate) {
                // Sofort mit NEUEM Array speichern (keine Closure-Falle)
                await setDoc(docRef, {
                    pastedMandanten: cleanMandanten,
                    externalLeads: cleanLeads,
                    lastCleaned: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                }, { merge: true });
                if (removedCount > 0) {
                    showMsg(`${removedCount} Mandanten-Duplikat(e) aus Gruppe C entfernt.`, 'info');
                }
            }
            setContactsChanged(false);
        } catch (error) {
            console.error("Fehler beim Laden der globalen Kontakte", error);
        } finally {
            setIsGlobalLoading(false);
        }
    }, [getSystemEmails, getAllMandantenEmails, purgeExternalLeadsAgainstMandanten, showMsg]);

    const loadCampaigns = useCallback(async () => {
        try {
            const q = query(collection(db, 'newsletter_campaigns'), orderBy('updatedAt', 'desc'));
            const querySnapshot = await getDocs(q);
            const loaded: Campaign[] = [];
            querySnapshot.forEach((doc) => {
                const data = doc.data();
                loaded.push({ 
                    id: doc.id, 
                    ...data,
                    targetClients: data.targetClients || data.targetBasic || data.targetInsider || false 
                } as Campaign);
            });
            setCampaigns(loaded);
        } catch (e) {
            console.error("Fehler beim Laden der Kampagnen", e);
        }
    }, []);

    // Nur beim Öffnen laden — NICHT wenn loadGlobalContacts sich durch State-Updates neu baut,
    // sonst überschreibt Firebase lokale Löschungen sofort wieder.
    useEffect(() => {
        if (!isOpen) return;
        void loadGlobalContacts();
        void loadCampaigns();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- absichtlich nur isOpen
    }, [isOpen]);

    useEffect(() => {
        return () => imagePreviews.forEach(url => URL.revokeObjectURL(url));
    }, [imagePreviews]);

    if (!isOpen) return null;

    const saveGlobalContacts = async () => {
        setIsGlobalLoading(true);
        try {
            const mandantenSet = getAllMandantenEmails(pastedMandanten);
            const { cleaned: purgedExternal, removedCount } = purgeExternalLeadsAgainstMandanten(
                externalLeads,
                mandantenSet
            );
            await persistGlobalContacts(pastedMandanten, purgedExternal, { silent: true });
            setExternalLeads(purgedExternal);
            setContactsChanged(false);
            if (removedCount > 0) {
                showMsg(`${removedCount} Mandanten-Duplikat(e) aus Gruppe C entfernt & gespeichert.`);
            } else {
                showMsg("Adressbücher global synchronisiert!");
            }
        } catch (error) {
            console.error(error);
            showMsg("Fehler beim Speichern der Listen.", "error");
        } finally {
            setIsGlobalLoading(false);
        }
    };

    const handleParseMandanten = async () => {
        if (!rawMandantenInput.trim()) return;
        const systemMails = getSystemEmails();
        const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
        const extracted = rawMandantenInput.match(emailRegex) || [];

        const newSet = new Set<string>(pastedMandanten.map(m => m.email.toLowerCase().trim()));

        extracted.forEach(mail => {
            const lowerMail = mail.toLowerCase().trim();
            if (!systemMails.has(lowerMail) && !newSet.has(lowerMail)) {
                newSet.add(lowerMail);
            }
        });

        const sortedArray = Array.from(new Set(newSet))
            .map(email => ({ email }))
            .sort((a, b) => a.email.localeCompare(b.email));

        // Nach B-Update: C gegen A ∪ B physisch purgen
        const mandantenSet = getAllMandantenEmails(sortedArray);
        const { cleaned: purgedExternal, removedCount } = purgeExternalLeadsAgainstMandanten(
            externalLeads,
            mandantenSet
        );

        setPastedMandanten(sortedArray);
        setExternalLeads(purgedExternal);
        setRawMandantenInput('');

        if (removedCount > 0) {
            setIsGlobalLoading(true);
            try {
                await persistGlobalContacts(sortedArray, purgedExternal, { silent: true });
                setContactsChanged(false);
                showMsg(`${removedCount} Lead(s) aus Gruppe C entfernt (jetzt Mandant).`);
            } catch (error) {
                console.error(error);
                setContactsChanged(true);
                showMsg('Bereinigung lokal ok — Speichern fehlgeschlagen.', 'error');
            } finally {
                setIsGlobalLoading(false);
            }
        } else {
            setContactsChanged(true);
        }
    };

    const removePastedMandant = async (email: string) => {
        const nextPasted = pastedMandanten.filter(
            (m) => m.email.toLowerCase().trim() !== email.toLowerCase().trim()
        );
        setPastedMandanten(nextPasted);
        setIsGlobalLoading(true);
        try {
            await persistGlobalContacts(nextPasted, externalLeads, { silent: true });
            setContactsChanged(false);
            showMsg('Mandant aus Gruppe B entfernt.', 'info');
        } catch (error) {
            console.error(error);
            setContactsChanged(true);
            showMsg('Löschen lokal — Speichern fehlgeschlagen.', 'error');
        } finally {
            setIsGlobalLoading(false);
        }
    };

    const handleParseExternal = () => {
        if (!rawExternalInput.trim()) return;
        // Konsequent gegen A ∪ B — keine neuen Mandanten-Duplikate in C
        const mandantenSet = getAllMandantenEmails();

        const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
        const extracted = rawExternalInput.match(emailRegex) || [];

        const { cleaned: baseLeads } = purgeExternalLeadsAgainstMandanten(externalLeads, mandantenSet);
        const newSet = new Set<string>(baseLeads);
        let skippedMandanten = 0;
        extracted.forEach(mail => {
            const lowerMail = mail.toLowerCase().trim();
            if (mandantenSet.has(lowerMail)) {
                skippedMandanten += 1;
                return;
            }
            if (!newSet.has(lowerMail)) {
                newSet.add(lowerMail);
            }
        });

        const sortedArray = Array.from(newSet).sort((a, b) => a.localeCompare(b));

        setExternalLeads(sortedArray);
        setRawExternalInput('');
        setContactsChanged(true);
        if (skippedMandanten > 0) {
            showMsg(`${skippedMandanten} Mandanten-Adresse(n) übersprungen (nicht in Gruppe C).`, 'info');
        }
    };

    const removeExternalLead = async (email: string) => {
        const nextExternal = externalLeads.filter(
            (e) => e.toLowerCase().trim() !== email.toLowerCase().trim()
        );
        setExternalLeads(nextExternal);
        setIsGlobalLoading(true);
        try {
            await persistGlobalContacts(pastedMandanten, nextExternal, { silent: true });
            setContactsChanged(false);
            showMsg('Lead aus Gruppe C entfernt.', 'info');
        } catch (error) {
            console.error(error);
            setContactsChanged(true);
            showMsg('Löschen lokal — Speichern fehlgeschlagen.', 'error');
        } finally {
            setIsGlobalLoading(false);
        }
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const filesArray = Array.from(e.target.files);
            const availableSlots = 3 - (existingImageUrls.length + images.length);
            const allowedFiles = filesArray.slice(0, availableSlots);
            
            if (allowedFiles.length > 0) {
                setImages([...images, ...allowedFiles]);
                setImagePreviews(prev => [...prev, ...allowedFiles.map(file => URL.createObjectURL(file))]);
            }
        }
    };

    const removeLocalImage = (index: number) => {
        const newImages = [...images];
        newImages.splice(index, 1);
        setImages(newImages);
        const newPreviews = [...imagePreviews];
        URL.revokeObjectURL(newPreviews[index]);
        newPreviews.splice(index, 1);
        setImagePreviews(newPreviews);
    };

    const removeExistingImage = (index: number) => {
        setExistingImageUrls(prev => prev.filter((_, i) => i !== index));
    };

    const processAndUploadImages = async () => {
        if (images.length === 0) return existingImageUrls; 

        showMsg("Lade Medien hoch...", "info");
        const storage = getStorage(db.app);
        let newlyUploadedUrls: string[] = [];

        for (const file of images) {
            const safeName = file.name.replace(/[^a-zA-Z0-9.]/g, '');
            const fileRef = ref(storage, `newsletter_media/${Date.now()}_${safeName}`);
            await uploadBytes(fileRef, file);
            const url = await getDownloadURL(fileRef);
            newlyUploadedUrls.push(url);
        }

        const finalUrls = [...existingImageUrls, ...newlyUploadedUrls];
        setExistingImageUrls(finalUrls);
        setImages([]);
        setImagePreviews([]);
        
        return finalUrls;
    };

    const handleSaveDraft = async () => {
        if (!subject.trim()) {
            showMsg("Bitte gib zumindest einen Betreff ein.", "error");
            return;
        }
        setIsGlobalLoading(true);
        try {
            const finalImageUrls = await processAndUploadImages();
            const safeMessage = optimizeHtmlForEmail(message);

            const payload = {
                status: 'draft',
                targetClients,
                targetExternal,
                subject,
                message: safeMessage,
                imageUrls: finalImageUrls, 
                updatedAt: new Date().toISOString()
            };

            if (currentDraftId) {
                await updateDoc(doc(db, 'newsletter_campaigns', currentDraftId), payload);
            } else {
                const docRef = await addDoc(collection(db, 'newsletter_campaigns'), payload);
                setCurrentDraftId(docRef.id);
            }
            
            showMsg("Entwurf erfolgreich gesichert!", "success");
            loadCampaigns(); 
        } catch (error) {
            console.error(error);
            showMsg("Fehler beim Speichern.", "error");
        } finally {
            setIsGlobalLoading(false);
        }
    };

    const handleSendTest = async () => {
        if (!testEmail.trim() || !testEmail.includes('@')) {
            showMsg("Bitte eine gültige E-Mail-Adresse für den Test eingeben.", "error");
            return;
        }
        if (!subject.trim() || !message.trim() || message === '<p><br></p>') {
            showMsg("Betreff und Nachricht dürfen für den Test nicht leer sein.", "error");
            return;
        }

        setIsTestSending(true);
        try {
            const finalImageUrls = await processAndUploadImages();
            const safeMessage = optimizeHtmlForEmail(message);

            showMsg("Versende Kampagne...", "info");
            const functionsInstance = getFunctions(db.app, 'europe-west1');
            const sendMailFn = httpsCallable(functionsInstance, 'sendNewsletter');
            
            const testAddress = testEmail.toLowerCase().trim();
            const nameByEmail = {
                ...externalLeadNames,
                ...collectCrmRecipientNames([...(crmClients || []), ...(pendingApprovals || [])]),
            };
            const result: any = await sendMailFn({
                subject: `[TEST] ${subject}`, 
                htmlContent: injectHalloGreetingHtml(safeMessage, nameByEmail[testAddress]), 
                imageUrls: finalImageUrls, 
                recipients: [testAddress], 
                senderName: "PreisExpert Team",
                senderEmail: "erfolg@friseur-coaching.de",
                appBaseUrl: window.location.origin,
                // Test → PEBase nur an User mit gleicher Anmelde-E-Mail (kein Massen-Fanout)
                mirrorToPebase: true,
            });

            const pebaseMirror = result?.data?.pebaseMirror;
            if (pebaseMirror?.ok && (pebaseMirror.recipients || 0) > 0) {
                showMsg(`Test an ${testEmail} + PEBase (${pebaseMirror.recipients}) versendet!`, "success");
            } else if (pebaseMirror?.warning) {
                showMsg(`Test-Mail OK. PEBase: ${pebaseMirror.warning}`, "success");
            } else if (pebaseMirror?.error) {
                showMsg(`Test-Mail OK, PEBase-Spiegel fehlgeschlagen.`, "success");
                console.warn("PEBase Mirror (Test):", pebaseMirror);
            } else {
                showMsg(`Test erfolgreich an ${testEmail} versendet!`, "success");
            }
            setTestEmail(''); 
        } catch (error) {
            console.error("Testversand fehlgeschlagen:", error);
            showMsg("Fehler beim Testversand (Gateway).", "error");
        } finally {
            setIsTestSending(false);
        }
    };

    const handleSendMassBroadcast = async () => {
        if (!subject.trim() || !message.trim() || message === '<p><br></p>') {
            showMsg("Betreff und Nachricht dürfen nicht leer sein.", "error");
            return;
        }
        if (!targetClients && !targetExternal) {
            showMsg("Bitte wähle mindestens eine Zielgruppe aus.", "error");
            return;
        }

        setIsGlobalLoading(true);
        const recipientsSet = new Set<string>();

        recipientsSet.add("erfolg@friseur-coaching.de"); 
        try {
            showMsg("Lade Coach-Verteiler...", "info");
            const sysDocRef = doc(db, 'system_data', 'global_settings');
            const sysDocSnap = await getDoc(sysDocRef);
            if (sysDocSnap.exists()) {
                const team = sysDocSnap.data().team || [];
                team.forEach((member: any) => {
                    if (member.email && member.email.includes('@')) {
                        recipientsSet.add(member.email.toLowerCase().trim());
                    }
                });
            }
        } catch (e) {
            console.error("Fehler beim Abrufen des Teams für den Newsletter-CC:", e);
        }

        if (targetClients) {
            const allClients = [...(crmClients || []), ...(pendingApprovals || [])];
            allClients.forEach(c => {
                if (c.email) recipientsSet.add(c.email.toLowerCase().trim());
                if (c.clientData?.mailadresse) recipientsSet.add(c.clientData.mailadresse.toLowerCase().trim());
            });
            
            pastedMandanten.forEach(m => {
                recipientsSet.add(m.email.toLowerCase().trim());
            });
        }

        if (targetExternal) {
            externalLeads.forEach(e => recipientsSet.add(e.toLowerCase().trim()));
        }

        const finalRecipients = Array.from(recipientsSet).filter(email => email.includes('@'));

        if (finalRecipients.length === 0) {
            showMsg("Keine gültigen Empfänger in den gewählten Gruppen gefunden.", "error");
            setIsGlobalLoading(false);
            return;
        }

        if (!window.confirm(`Sicherheitsabfrage: Möchtest du diesen Newsletter jetzt WIRKLICH an ${finalRecipients.length} Empfänger (inklusive Team) senden?`)) {
            setIsGlobalLoading(false);
            return;
        }

        try {
            const finalImageUrls = await processAndUploadImages();
            const safeMessage = optimizeHtmlForEmail(message);

            showMsg(`Versende an ${finalRecipients.length} Empfänger...`, "info");
            const functionsInstance = getFunctions(db.app, 'europe-west1');
            const sendMailFn = httpsCallable(functionsInstance, 'sendNewsletter', { timeout: 300000 });
            const nameByEmail = {
                ...externalLeadNames,
                ...collectCrmRecipientNames([...(crmClients || []), ...(pendingApprovals || [])]),
            };
            const groups = htmlAlreadyHasHallo(safeMessage)
                ? [{ firstName: null as string | null, emails: finalRecipients }]
                : groupRecipientsByFirstName(finalRecipients, nameByEmail);

            let sentCount = 0;
            let failedCount = 0;
            let pebaseMirror: any = null;
            let lastFailedEmails: any = undefined;

            for (let i = 0; i < groups.length; i += 1) {
                const group = groups[i];
                if (groups.length > 1) {
                    showMsg(`Versende Anrede ${i + 1} von ${groups.length}…`, 'info');
                }
                const result: any = await sendMailFn({
                    subject: subject, 
                    htmlContent: injectHalloGreetingHtml(safeMessage, group.firstName), 
                    imageUrls: finalImageUrls, 
                    recipients: group.emails, 
                    senderName: "PreisExpert System",
                    senderEmail: "erfolg@friseur-coaching.de",
                    appBaseUrl: window.location.origin,
                    targetClients,
                    targetExternal,
                    mirrorToPebase: true,
                });
                sentCount += result?.data?.sentCount ?? group.emails.length;
                failedCount += result?.data?.failedCount ?? 0;
                if (result?.data?.pebaseMirror) pebaseMirror = result.data.pebaseMirror;
                if (result?.data?.failedEmails) lastFailedEmails = result.data.failedEmails;
            }

            const payload = {
                status: 'sent',
                targetClients,
                targetExternal,
                subject,
                message: safeMessage,
                imageUrls: finalImageUrls,
                sentAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                sentCount,
                failedCount,
            };

            if (currentDraftId) {
                await updateDoc(doc(db, 'newsletter_campaigns', currentDraftId), payload);
            } else {
                await addDoc(collection(db, 'newsletter_campaigns'), payload);
            }

            if (failedCount > 0) {
                showMsg(`Teilversand: ${sentCount} ok, ${failedCount} fehlgeschlagen. Details in der Konsole.`, "error");
                console.warn("Newsletter Teilfehler:", lastFailedEmails);
            } else if (pebaseMirror?.ok) {
                showMsg(`Erfolgreich an ${sentCount} Empfänger versendet (+ PEBase an ${pebaseMirror.recipients || 0})!`, "success");
            } else if (pebaseMirror?.error) {
                showMsg(`E-Mail ok (${sentCount}), PEBase-Spiegel fehlgeschlagen — siehe Konsole.`, "error");
                console.warn("PEBase Mirror:", pebaseMirror);
            } else {
                showMsg(`Erfolgreich an ${sentCount} Empfänger versendet!`, "success");
            }
            loadCampaigns();
            resetEditor();
        } catch (error: any) {
            console.error("Versand fehlgeschlagen:", error);
            const detail = error?.message || error?.details || "Unbekannter Fehler";
            showMsg(`Fehler beim Massenversand: ${detail}`, "error");
        } finally {
            setIsGlobalLoading(false);
        }
    };

    const loadDraftIntoEditor = (campaign: Campaign) => {
        setCurrentDraftId(campaign.id);
        setTargetClients(campaign.targetClients || false);
        setTargetExternal(campaign.targetExternal || false);
        setSubject(campaign.subject || '');
        setMessage(campaign.message || '');
        
        setExistingImageUrls(campaign.imageUrls || []);
        setImages([]); 
        setImagePreviews([]);
        
        setActiveTab('compose');
        showMsg("Entwurf in den Editor geladen.", "info");
    };

    const loadAsTemplate = (campaign: Campaign) => {
        setCurrentDraftId(null);
        setTargetClients(campaign.targetClients || false);
        setTargetExternal(campaign.targetExternal || false);
        setSubject(campaign.subject || '');
        setMessage(campaign.message || '');

        setExistingImageUrls(campaign.imageUrls || []);
        setImages([]); 
        setImagePreviews([]);
        
        setActiveTab('compose');
        showMsg("Als neue Vorlage in den Editor geladen.", "info");
    };

    const deleteCampaign = async (id: string) => {
        if (!window.confirm("Diesen Eintrag wirklich löschen?")) return;
        setIsGlobalLoading(true);
        try {
            await deleteDoc(doc(db, 'newsletter_campaigns', id));
            if (currentDraftId === id) resetEditor();
            loadCampaigns();
            showMsg("Eintrag gelöscht.", "info");
        } catch(e) {
            console.error(e);
            showMsg("Fehler beim Löschen.", "error");
        } finally {
            setIsGlobalLoading(false);
        }
    };

    const resetEditor = () => {
        setCurrentDraftId(null);
        setTargetClients(false);
        setTargetExternal(false);
        setSubject('');
        setMessage('');
        setExistingImageUrls([]);
        setImages([]);
        setImagePreviews([]);
    };

    const draftsList = campaigns.filter(c => c.status === 'draft');
    const archiveList = campaigns.filter(c => c.status === 'sent');
    const totalImageCount = existingImageUrls.length + images.length;

    return (
        <div className="fixed inset-0 z-[100000] bg-black/90 backdrop-blur-md flex items-center justify-center p-4">
            
            <style dangerouslySetInnerHTML={{__html: `
                .quill-dark-wrapper .ql-toolbar.ql-snow { position: sticky; top: 0; z-index: 10; border-color: #1e293b; background: #0f1015; border-radius: 8px 8px 0 0; }
                .quill-dark-wrapper .ql-container.ql-snow { border-color: #1e293b; color: #f8fafc; font-size: 15px; font-family: inherit; border-radius: 0 0 8px 8px; background: #0a0b0e; }
                .quill-dark-wrapper .ql-editor {
                    min-height: 320px;
                    padding: 28px 32px;
                    line-height: 1.75;
                }
                .quill-dark-wrapper .ql-editor p { margin: 0 0 1em; }
                .quill-dark-wrapper .ql-editor p:last-child { margin-bottom: 0; }
                .quill-dark-wrapper .ql-snow .ql-stroke { stroke: #cbd5e1; }
                .quill-dark-wrapper .ql-snow .ql-fill { fill: #cbd5e1; }
                .quill-dark-wrapper .ql-snow .ql-picker { color: #cbd5e1; }
                .quill-dark-wrapper .ql-editor.ql-blank::before { color: #475569; font-style: normal; }
                .quill-dark-wrapper .ql-editor img { 
                    display: block !important;
                    max-width: 320px !important; 
                    width: 100%;
                    height: auto;
                    margin: 8px auto 24px !important; 
                    border-radius: 8px; 
                    transition: all 0.2s ease-in-out; 
                    border: 2px solid transparent; 
                    cursor: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"><circle cx="12" cy="12" r="11" fill="%23ef4444" stroke="white" stroke-width="2"/><path d="M8 8l8 8m0-8l-8 8" stroke="white" stroke-width="2" stroke-linecap="round"/></svg>') 16 16, pointer !important;
                }
                @media only screen and (max-width: 620px) {
                    .quill-dark-wrapper .ql-editor {
                        padding: 20px 18px;
                    }
                    .quill-dark-wrapper .ql-editor img {
                        max-width: 100% !important;
                        margin: 8px auto 20px !important;
                    }
                }
                .quill-dark-wrapper .ql-editor img:hover { 
                    opacity: 0.6; 
                    border-color: #ef4444; 
                    box-shadow: 0 0 15px rgba(239, 68, 68, 0.4);
                }
            `}} />

            <div className="bg-[#0f1015] border border-blue-500/30 rounded-2xl max-w-6xl w-full h-[90vh] shadow-[0_0_80px_rgba(59,130,246,0.15)] animate-fade-in relative flex flex-col overflow-hidden">
                
                {/* GLOBAL HEADER */}
                <div className="px-8 py-4 border-b border-slate-800 bg-[#15171e] flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-blue-500/10 rounded-xl flex items-center justify-center border border-blue-500/20">
                            <MailIcon size={20} className="text-blue-400" />
                        </div>
                        <div>
                            <h2 className="text-lg font-black text-white tracking-widest uppercase mb-0.5 flex items-center gap-3">
                                Broadcasting Hub
                                {isGlobalLoading && <span className="text-[9px] bg-blue-900/30 text-blue-400 border border-blue-800 px-2 py-0.5 rounded-full flex items-center gap-1 animate-pulse"><RefreshIcon size={8} className="animate-spin" /> SYNC...</span>}
                                {statusMsg && (
                                    <span className={`text-[9px] border px-2 py-0.5 rounded-full flex items-center gap-1 ${statusMsg.type === 'error' ? 'bg-red-900/30 text-red-400 border-red-800' : statusMsg.type === 'info' ? 'bg-slate-800 text-slate-300 border-slate-700' : 'bg-emerald-900/30 text-emerald-400 border-emerald-800'}`}>
                                        {statusMsg.msg}
                                    </span>
                                )}
                            </h2>
                            <p className="text-[10px] text-slate-400">Zentrale für Newsletter, Kampagnen und globale Adressbücher.</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 bg-slate-800 hover:bg-slate-700 rounded-full text-slate-400 transition-colors">
                        <XIcon size={20} />
                    </button>
                </div>

                <div className="flex flex-1 overflow-hidden">
                    {/* SIDEBAR */}
                    <aside className="w-64 border-r border-slate-800 bg-[#15171e] flex flex-col shrink-0">
                        <div className="p-4 space-y-2">
                            <button 
                                onClick={() => setActiveTab('compose')}
                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-bold text-xs uppercase tracking-widest transition-all ${activeTab === 'compose' ? 'bg-blue-600 text-white shadow-[0_0_15px_rgba(37,99,235,0.3)]' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
                            >
                                <EditIcon size={16} /> Neues Mailing
                            </button>
                            <button 
                                onClick={() => setActiveTab('contacts')}
                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-bold text-xs uppercase tracking-widest transition-all ${activeTab === 'contacts' ? 'bg-slate-800 text-[#E6C299] border border-[#E6C299]/30' : 'text-slate-400 hover:bg-slate-800 hover:text-white border border-transparent'}`}
                            >
                                <BookIcon size={16} /> Adressbücher
                                {(pastedMandanten.length > 0 || externalLeads.length > 0) && (
                                    <span className="ml-auto bg-slate-900 text-slate-500 px-1.5 py-0.5 rounded text-[9px]">{pastedMandanten.length + externalLeads.length}</span>
                                )}
                            </button>
                        </div>
                        
                        <div className="mx-4 my-2 border-t border-slate-800"></div>

                        <div className="p-4 space-y-2">
                            <button 
                                onClick={() => setActiveTab('drafts')}
                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-bold text-xs uppercase tracking-widest transition-all ${activeTab === 'drafts' ? 'bg-slate-800 text-white border border-slate-700' : 'text-slate-400 hover:bg-slate-800 hover:text-white border border-transparent'}`}
                            >
                                <FileTextIcon size={16} /> Entwürfe
                                {draftsList.length > 0 && <span className="ml-auto bg-slate-900 text-slate-500 px-1.5 py-0.5 rounded text-[9px]">{draftsList.length}</span>}
                            </button>
                            <button 
                                onClick={() => setActiveTab('archive')}
                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-bold text-xs uppercase tracking-widest transition-all ${activeTab === 'archive' ? 'bg-slate-800 text-emerald-400 border border-slate-700' : 'text-slate-400 hover:bg-slate-800 hover:text-white border border-transparent'}`}
                            >
                                <ArchiveIcon size={16} /> Archiv / Gesendet
                                {archiveList.length > 0 && <span className="ml-auto bg-slate-900 text-slate-500 px-1.5 py-0.5 rounded text-[9px]">{archiveList.length}</span>}
                            </button>
                        </div>
                    </aside>

                    {/* MAIN CONTENT AREA */}
                    <div className="flex-1 bg-[#0a0b0e] flex flex-col relative overflow-hidden">
                        <main className="flex-1 overflow-y-auto custom-scrollbar p-8">
                            {/* TAB 1: COMPOSE */}
                            {activeTab === 'compose' && (
                                <div className="space-y-8 animate-fade-in max-w-4xl mx-auto pb-4">
                                    <div className="flex items-center justify-between border-b border-slate-800 pb-4">
                                        <div>
                                            <h3 className="text-xl font-black text-white uppercase tracking-widest">
                                                {currentDraftId ? 'Entwurf bearbeiten' : 'Neue Kampagne erstellen'}
                                            </h3>
                                            <p className="text-xs text-slate-500 mt-1">Definiere Zielgruppen und erstelle deinen Content.</p>
                                        </div>
                                        {currentDraftId && (
                                            <button onClick={resetEditor} className="text-[10px] bg-slate-800 hover:bg-slate-700 text-slate-300 px-3 py-1.5 rounded uppercase font-bold tracking-widest transition-colors flex items-center gap-2">
                                                <XIcon size={12} /> Editor leeren
                                            </button>
                                        )}
                                    </div>

                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between mb-2">
                                            <h4 className="text-xs font-black text-[#E6C299] uppercase tracking-widest flex items-center gap-2">
                                                <ActivityIcon size={14} /> 1. Verteiler wählen
                                            </h4>
                                            <div className={`text-xs font-black px-3 py-1 rounded-full border ${liveRecipientCount > 480 ? 'bg-red-900/30 text-red-400 border-red-500/50 animate-pulse' : 'bg-[#15171e] text-[#E6C299] border-slate-700'}`}>
                                                Empfänger gesamt: {liveRecipientCount}
                                            </div>
                                        </div>

                                        <div className="bg-[#15171e] border border-slate-800 rounded-xl p-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                                            <label className={`flex items-center p-4 rounded-lg cursor-pointer transition-colors border ${targetClients ? 'bg-blue-900/20 border-blue-500/50' : 'bg-[#0a0b0e] border-slate-800 hover:border-slate-600'}`}>
                                                <input type="checkbox" checked={targetClients} onChange={(e) => setTargetClients(e.target.checked)} className="w-5 h-5 rounded border-slate-600 text-blue-500 focus:ring-blue-500 bg-[#08090b]" />
                                                <div className="ml-3">
                                                    <span className={`text-xs font-bold uppercase tracking-wide block ${targetClients ? 'text-blue-400' : 'text-slate-300'}`}>Alle Mandanten</span>
                                                    <span className="text-[9px] text-slate-500">CRM & Importierte Mandanten</span>
                                                </div>
                                            </label>
                                            <label className={`flex items-center p-4 rounded-lg cursor-pointer transition-colors border ${targetExternal ? 'bg-emerald-900/20 border-emerald-500/50' : 'bg-[#0a0b0e] border-slate-800 hover:border-slate-600'}`}>
                                                <input type="checkbox" checked={targetExternal} onChange={(e) => setTargetExternal(e.target.checked)} className="w-5 h-5 rounded border-slate-600 text-emerald-500 focus:ring-emerald-500 bg-[#08090b]" />
                                                <div className="ml-3">
                                                    <span className={`text-xs font-bold uppercase tracking-wide block ${targetExternal ? 'text-emerald-400' : 'text-slate-300'}`}>Externe Leads</span>
                                                    <span className="text-[9px] text-slate-500">Nur globale Liste C</span>
                                                </div>
                                            </label>
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <h4 className="text-xs font-black text-[#E6C299] uppercase tracking-widest flex items-center gap-2">
                                            <MailIcon size={14} /> 2. E-Mail Inhalt
                                        </h4>
                                        <div className="bg-[#15171e] border border-slate-800 rounded-xl p-6 space-y-4">
                                            <div>
                                                <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-2 block">Betreff *</label>
                                                <input 
                                                    type="text" 
                                                    value={subject}
                                                    onChange={(e) => setSubject(e.target.value)}
                                                    placeholder="Dein einprägsamer Betreff..." 
                                                    className="w-full bg-[#0a0b0e] border border-slate-700 text-white rounded-lg p-3 text-sm focus:border-blue-500 outline-none font-bold placeholder-slate-700" 
                                                />
                                            </div>
                                            <div>
                                                <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-2 block flex justify-between">
                                                    Nachrichtentext
                                                </label>
                                                <div className="quill-dark-wrapper" onClick={handleEditorImageClick}>
                                                    <ReactQuill 
                                                        ref={quillRef} 
                                                        theme="snow" 
                                                        value={message} 
                                                        onChange={setMessage} 
                                                        modules={quillModules}
                                                        placeholder="Schreibe hier deine formatierte Nachricht..."
                                                    />
                                                </div>
                                            </div>
                                             
                                            <div className="pt-2 border-t border-slate-800/50">
                                                <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-2 block flex justify-between">
                                                    Medien (Max. 3)
                                                    <span className="text-slate-600 font-normal normal-case">{totalImageCount} / 3</span>
                                                </label>
                                                <div className="flex gap-4">
                                                    <button 
                                                        onClick={() => fileInputRef.current?.click()}
                                                        disabled={totalImageCount >= 3}
                                                        className={`flex flex-col items-center justify-center w-24 h-24 rounded-xl border border-dashed transition-colors ${totalImageCount >= 3 ? 'bg-slate-900 border-slate-800 text-slate-700 cursor-not-allowed' : 'bg-[#0a0b0e] border-slate-700 text-slate-400 hover:border-[#E6C299] hover:text-[#E6C299]'}`}
                                                    >
                                                        <ImageIcon size={20} className="mb-1" />
                                                        <span className="text-[9px] font-bold uppercase tracking-widest text-center px-2">Bild<br/>hinzu</span>
                                                    </button>
                                                    <input type="file" ref={fileInputRef} onChange={handleImageChange} accept="image/*" multiple className="hidden" />

                                                    {existingImageUrls.map((url, index) => (
                                                        <div key={`ext-${index}`} className="relative w-24 h-24 rounded-xl border border-slate-700 bg-[#0a0b0e] overflow-hidden group">
                                                            <img src={url} alt="Cloud Vorschau" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                                                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                                <button onClick={() => removeExistingImage(index)} className="p-2 bg-red-600 text-white rounded-full hover:bg-red-500 transform scale-75 group-hover:scale-100 transition-all shadow-lg">
                                                                    <TrashIcon size={14} />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}

                                                    {imagePreviews.map((preview, index) => (
                                                        <div key={`loc-${index}`} className="relative w-24 h-24 rounded-xl border border-slate-700 bg-[#0a0b0e] overflow-hidden group">
                                                            <img src={preview} alt="Lokale Vorschau" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                                                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                                <button onClick={() => removeLocalImage(index)} className="p-2 bg-red-600 text-white rounded-full hover:bg-red-500 transform scale-75 group-hover:scale-100 transition-all shadow-lg">
                                                                    <TrashIcon size={14} />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* TAB 2: CONTACTS */}
                            {activeTab === 'contacts' && (
                                <div className="space-y-8 animate-fade-in max-w-4xl mx-auto pb-24">
                                    <div className="flex flex-col md:flex-row md:items-end justify-between border-b border-slate-800 pb-4 gap-4">
                                        <div>
                                            <h3 className="text-xl font-black text-white uppercase tracking-widest flex items-center gap-3">
                                                Globale Adressbücher
                                            </h3>
                                            <p className="text-xs text-slate-500 mt-1">Hier pflegst du Listen, die unabhängig von einzelnen Kampagnen existieren.</p>
                                        </div>
                                        <button 
                                            onClick={saveGlobalContacts}
                                            disabled={!contactsChanged || isGlobalLoading}
                                            className={`px-6 py-2 rounded uppercase text-xs font-black tracking-widest transition-all flex items-center gap-2 ${contactsChanged ? 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-[0_0_15px_rgba(16,185,129,0.3)]' : 'bg-slate-800 text-slate-500 border border-slate-700 cursor-not-allowed'}`}
                                        >
                                            <SaveIcon size={14} /> Listen in Cloud sichern
                                        </button>
                                    </div>

                                    <div className="bg-[#15171e] border border-slate-800 rounded-xl p-6">
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center gap-2">
                                                <BriefcaseIcon size={18} className="text-[#E6C299]" />
                                                <h4 className="text-sm font-black text-white uppercase tracking-widest">Gruppe B: Manuelle Mandanten (Import)</h4>
                                            </div>
                                            <span className="text-[10px] text-slate-400 bg-[#0a0b0e] border border-slate-800 px-3 py-1 rounded-full font-mono">Gespeichert: {pastedMandanten.length}</span>
                                        </div>
                                        <div className="flex gap-2 mb-4">
                                            <textarea 
                                                value={rawMandantenInput}
                                                onChange={(e) => setRawMandantenInput(e.target.value)}
                                                placeholder="Excel-Spalte einfügen... (CRM-Mandanten werden beim Import gefiltert)"
                                                className="w-full h-12 bg-[#0a0b0e] border border-slate-700 text-slate-300 rounded-lg p-3 text-xs focus:border-[#E6C299] outline-none resize-none font-mono placeholder-slate-600"
                                            />
                                            <button 
                                                onClick={handleParseMandanten}
                                                disabled={!rawMandantenInput.trim()}
                                                className={`flex flex-col items-center justify-center px-4 rounded-lg font-bold text-[10px] uppercase tracking-widest transition-colors ${rawMandantenInput.trim() ? 'bg-[#E6C299]/20 text-[#E6C299] border border-[#E6C299]/50 hover:bg-[#E6C299] hover:text-black' : 'bg-[#0a0b0e] text-slate-600 border border-slate-800 cursor-not-allowed'}`}
                                            >
                                                <FilterIcon size={16} className="mb-1" /> Add
                                            </button>
                                        </div>
                                        {pastedMandanten.length > 0 && (
                                            <div className="bg-[#0a0b0e] border border-slate-800 rounded-lg p-4 max-h-[400px] overflow-y-auto custom-scrollbar">
                                                <div className="flex flex-wrap gap-2">
                                                    {pastedMandanten.map(m => (
                                                        <div key={m.email} className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-mono border transition-colors bg-slate-800 border-slate-600 text-white`}>
                                                            <span>{m.email}</span>
                                                            <div className="w-px h-3 bg-slate-600 mx-1"></div>
                                                            <button onClick={() => removePastedMandant(m.email)} className="text-slate-500 hover:text-red-400"><XIcon size={12} /></button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="bg-[#15171e] border border-slate-800 rounded-xl p-6">
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center gap-2">
                                                <GlobeIcon size={18} className="text-emerald-500" />
                                                <h4 className="text-sm font-black text-white uppercase tracking-widest">Gruppe C: Externe Leads / Schulungen</h4>
                                            </div>
                                            <span className="text-[10px] text-slate-400 bg-[#0a0b0e] border border-slate-800 px-3 py-1 rounded-full font-mono">Gespeichert: {externalLeads.length}</span>
                                        </div>
                                        <div className="flex gap-2 mb-4">
                                            <textarea 
                                                value={rawExternalInput}
                                                onChange={(e) => setRawExternalInput(e.target.value)}
                                                placeholder="Excel-Spalte einfügen... (Mandanten aus A & B werden ignoriert)"
                                                className="w-full h-12 bg-[#0a0b0e] border border-slate-700 text-slate-300 rounded-lg p-3 text-xs focus:border-emerald-500 outline-none resize-none font-mono placeholder-slate-600"
                                            />
                                            <button 
                                                onClick={handleParseExternal}
                                                disabled={!rawExternalInput.trim()}
                                                className={`flex flex-col items-center justify-center px-4 rounded-lg font-bold text-[10px] uppercase tracking-widest transition-colors ${rawExternalInput.trim() ? 'bg-emerald-900/30 text-emerald-400 border border-emerald-500/50 hover:bg-emerald-600 hover:text-white' : 'bg-[#0a0b0e] text-slate-600 border border-slate-800 cursor-not-allowed'}`}
                                            >
                                                <FilterIcon size={16} className="mb-1" /> Add
                                            </button>
                                        </div>
                                        {externalLeads.length > 0 && (
                                            <div className="bg-[#0a0b0e] border border-slate-800 rounded-lg p-4 max-h-[400px] overflow-y-auto custom-scrollbar">
                                                <div className="flex flex-wrap gap-2">
                                                    {externalLeads.map(email => (
                                                        <div key={email} className="bg-slate-800 text-slate-300 border border-slate-700 rounded-full px-3 py-1.5 text-[10px] font-mono flex items-center gap-2 group">
                                                            {email}
                                                            <button onClick={() => removeExternalLead(email)} className="text-slate-500 hover:text-red-400 focus:outline-none"><XIcon size={12} /></button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* TAB 3: DRAFTS */}
                            {activeTab === 'drafts' && (
                                <div className="space-y-6 animate-fade-in max-w-4xl mx-auto pb-24">
                                    <div className="border-b border-slate-800 pb-4 flex justify-between items-end">
                                        <div>
                                            <h3 className="text-xl font-black text-white uppercase tracking-widest flex items-center gap-3">
                                                <FileTextIcon size={24} className="text-blue-400" /> Gespeicherte Entwürfe
                                            </h3>
                                            <p className="text-xs text-slate-500 mt-1">Kampagnen, die noch nicht versendet wurden.</p>
                                        </div>
                                        <button onClick={loadCampaigns} className="p-2 bg-[#15171e] hover:bg-slate-800 border border-slate-700 rounded text-slate-400 transition-colors"><RefreshIcon size={14}/></button>
                                    </div>

                                    {draftsList.length === 0 ? (
                                        <div className="bg-[#15171e] border border-slate-800 border-dashed rounded-xl p-16 flex flex-col items-center text-center">
                                            <FileTextIcon size={48} className="text-slate-700 mb-4" />
                                            <h4 className="text-white font-bold mb-1">Keine Entwürfe gefunden.</h4>
                                            <p className="text-xs text-slate-500">Du kannst unter "Neues Mailing" einen Entwurf anlegen.</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            {draftsList.map(draft => (
                                                <div key={draft.id} className="bg-[#15171e] border border-slate-800 rounded-xl p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:border-slate-600 transition-colors group">
                                                    <div className="flex-1 min-w-0">
                                                        <h4 className="text-white font-bold text-sm truncate">{draft.subject || '(Ohne Betreff)'}</h4>
                                                        <div className="flex items-center gap-4 mt-2">
                                                            <span className="text-[10px] text-slate-500 font-mono flex items-center gap-1"><ClockIcon size={10}/> {new Date(draft.updatedAt).toLocaleString('de-DE')}</span>
                                                            <div className="flex gap-1">
                                                                {draft.targetClients && <span className="bg-blue-900/30 text-blue-400 px-1.5 py-0.5 rounded text-[8px] uppercase font-bold">Mandanten</span>}
                                                                {draft.targetExternal && <span className="bg-emerald-900/30 text-emerald-400 px-1.5 py-0.5 rounded text-[8px] uppercase font-bold">Extern</span>}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-3 shrink-0">
                                                        <button onClick={() => deleteCampaign(draft.id)} className="p-2 text-slate-600 hover:text-red-400 hover:bg-red-900/20 rounded-lg transition-colors" title="Entwurf löschen"><TrashIcon size={16}/></button>
                                                        <button onClick={() => loadDraftIntoEditor(draft)} className="px-4 py-2 bg-slate-800 hover:bg-blue-600 hover:border-blue-500 text-white text-[10px] font-black uppercase tracking-widest border border-slate-700 rounded-lg transition-colors shadow-lg flex items-center gap-2">
                                                            <EditIcon size={12} /> Bearbeiten
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* TAB 4: ARCHIVE */}
                            {activeTab === 'archive' && (
                                <div className="space-y-6 animate-fade-in max-w-4xl mx-auto pb-24">
                                    <div className="border-b border-slate-800 pb-4 flex justify-between items-end">
                                        <div>
                                            <h3 className="text-xl font-black text-emerald-500 uppercase tracking-widest flex items-center gap-3">
                                                <ArchiveIcon size={24} /> Archiv (Gesendet)
                                            </h3>
                                            <p className="text-xs text-slate-500 mt-1">Historie aller erfolgreich verschickten Kampagnen.</p>
                                        </div>
                                        <button onClick={loadCampaigns} className="p-2 bg-[#15171e] hover:bg-slate-800 border border-slate-700 rounded text-slate-400 transition-colors"><RefreshIcon size={14}/></button>
                                    </div>

                                    {archiveList.length === 0 ? (
                                        <div className="bg-[#15171e] border border-slate-800 border-dashed rounded-xl p-16 flex flex-col items-center text-center">
                                            <ArchiveIcon size={48} className="text-slate-700 mb-4" />
                                            <h4 className="text-white font-bold mb-1">Archiv ist leer.</h4>
                                            <p className="text-xs text-slate-500">Bisher wurden noch keine Mailings über das Gateway verschickt.</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            {archiveList.map(camp => (
                                                <div key={camp.id} className="bg-[#15171e] border border-slate-800 rounded-xl p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 opacity-80 hover:opacity-100 transition-opacity">
                                                    <div className="flex-1 min-w-0">
                                                        <h4 className="text-slate-300 font-bold text-sm truncate">{camp.subject}</h4>
                                                        <div className="flex items-center gap-4 mt-2">
                                                            <span className="text-[10px] text-emerald-500/70 font-mono flex items-center gap-1"><CheckCircleIcon size={10}/> Gesendet: {camp.sentAt ? new Date(camp.sentAt).toLocaleString('de-DE') : 'Unbekannt'}</span>
                                                            <div className="flex gap-1 opacity-50">
                                                                {camp.targetClients && <span className="bg-slate-800 text-slate-400 px-1.5 py-0.5 rounded text-[8px] uppercase font-bold">Mandanten</span>}
                                                                {camp.targetExternal && <span className="bg-slate-800 text-slate-400 px-1.5 py-0.5 rounded text-[8px] uppercase font-bold">Extern</span>}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-3 shrink-0">
                                                        <button onClick={() => deleteCampaign(camp.id)} className="p-2 text-slate-700 hover:text-red-400 hover:bg-red-900/20 rounded-lg transition-colors" title="Aus Archiv löschen"><TrashIcon size={14}/></button>
                                                        <button onClick={() => loadAsTemplate(camp)} className="px-4 py-2 bg-slate-800 hover:bg-emerald-600 hover:border-emerald-500 text-white text-[10px] font-black uppercase tracking-widest border border-slate-700 rounded-lg transition-colors shadow-lg flex items-center gap-2">
                                                            <CopyIcon size={12} /> Als Vorlage
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </main>

                        {/* COMPOSE FOOTER - FIXIERT UNTEN */}
                        {activeTab === 'compose' && (
                            <div className="bg-[#15171e]/95 backdrop-blur border-t border-slate-800 p-4 flex justify-between items-center z-50 px-8 shrink-0">
                                <div className="flex items-center gap-2 bg-[#0a0b0e] border border-slate-800 p-1.5 rounded-xl">
                                    <input
                                        type="email"
                                        value={testEmail}
                                        onChange={(e) => setTestEmail(e.target.value)}
                                        placeholder="Test E-Mail..."
                                        className="bg-transparent text-xs text-white px-3 py-2 w-48 outline-none placeholder-slate-600 font-mono"
                                    />
                                    <button
                                        onClick={handleSendTest}
                                        disabled={isTestSending || !testEmail.trim() || !subject.trim()}
                                        className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-[#E6C299] rounded-lg font-bold text-[10px] uppercase tracking-widest transition-all flex items-center gap-2 disabled:opacity-50"
                                    >
                                        {isTestSending ? <RefreshIcon size={14} className="animate-spin" /> : <FlaskIcon size={14} />}
                                        Test senden
                                    </button>
                                </div>

                                <div className="flex gap-4">
                                    <button 
                                        onClick={handleSaveDraft}
                                        disabled={isGlobalLoading}
                                        className="px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white border border-slate-700 rounded-lg font-bold text-xs uppercase tracking-widest transition-all flex items-center gap-2"
                                    >
                                        <SaveIcon size={16} /> Kampagne speichern
                                    </button>
                                    <button 
                                        onClick={handleSendMassBroadcast}
                                        disabled={isGlobalLoading || (!targetClients && !targetExternal)}
                                        className="px-8 py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-black uppercase tracking-widest text-xs transition-all shadow-[0_0_15px_rgba(37,99,235,0.3)] flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <SendIcon size={16} /> Versand starten
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminNewsletterModal;